// Adapted for Vite + react-i18next

import { useTranslation } from 'react-i18next'
import { ESmsLogSource, ESmsLogStatuses, TSmsLog } from 'src/types/sms-log'
import { CurecallTag, CurecallTooltip } from '@honestica/curecall-ui'
import { useSession } from 'src/providers/auth.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TimezoneAwareTime } from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'
import { config } from 'src/config'
import { getBackofficeWorkflowUrl } from 'src/utils/backoffice.util'
import { twMerge } from 'src/utils/tw-merge.util'

const OutboundSmsLogCard = ({ smsLog, className }: { smsLog: TSmsLog; className?: string }) => {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  const { project } = useCurrentProject()

  const isCurrentUserAdmin = currentUser?.isAdmin
  const isSmsSentByCurecall =
    smsLog.source &&
    [ESmsLogSource.COPILOT, ESmsLogSource.CHATBOT, ESmsLogSource.WORKFLOW].includes(smsLog.source)
  const isSmsSentByUser = smsLog.source === ESmsLogSource.USER

  const smsStatusToLabel: Record<string, { tagLabel: string; tagColor: string; tooltipContent: string }> = {
    [ESmsLogStatuses.SENDING]: {
      tagLabel: t('components.sms.statuses.sending'),
      tagColor: 'WARNING',
      tooltipContent: t('components.sms.statuses.sending-details'),
    },
    [ESmsLogStatuses.DELIVERED]: {
      tagLabel: t('components.sms.statuses.delivered'),
      tagColor: 'SUCCESS',
      tooltipContent: t('components.sms.statuses.delivered-details'),
    },
    [ESmsLogStatuses.READ]: {
      tagLabel: t('components.sms.statuses.read'),
      tagColor: 'SUCCESS',
      tooltipContent: t('components.sms.statuses.read-details'),
    },
    [ESmsLogStatuses.PROVIDER_ERROR]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.provider-error'),
    },
    [ESmsLogStatuses.ERROR]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.error'),
    },
    [ESmsLogStatuses.BLOCKED_WHITELIST]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.error'),
    },
    [ESmsLogStatuses.UNSUPPORTED_CHANNEL]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.error'),
    },
    [ESmsLogStatuses.BLOCKED_NO_CONSENT]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.blocked-no-consent'),
    },
    [ESmsLogStatuses.BLOCKED_ARCHIVED]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.blocked-archived'),
    },
    [ESmsLogStatuses.BLOCKED_INVALID_PHONE]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.blocked-invalid-phone'),
    },
    [ESmsLogStatuses.BLOCKED_NO_COMMUNICATION_CHANNEL]: {
      tagLabel: t('components.sms.statuses.not-delivered'),
      tagColor: 'DANGER',
      tooltipContent: t('components.sms.statuses.blocked-no-communication-channel'),
    },
  }

  const fallbackStatusLabel = {
    tagLabel: t('components.sms.statuses.not-delivered'),
    tagColor: 'DANGER',
    tooltipContent: t('components.sms.statuses.error'),
  }
  const statusLabel = smsStatusToLabel[smsLog.status] ?? fallbackStatusLabel

  let linkUrl = null
  if (isCurrentUserAdmin) {
    const backofficeUrl = config.BACKOFFICE_URL
    if (backofficeUrl) {
      if (smsLog.SentByConversationId) {
        linkUrl = `${backofficeUrl}/projects/${project.id}/conversations/${smsLog.SentByConversationId}/configure`
      } else if (smsLog.SentByWorkflowId) {
        linkUrl = getBackofficeWorkflowUrl(project.id, smsLog.SentByWorkflowId)
      }
    }
  }

  return (
    <div className={twMerge('c-flex c-flex-col c-w-3/4', className)}>
      <div className="c-flex c-gap-2 c-pt-1 c-items-center">
        {isSmsSentByCurecall && (
          <p className="c-text-primary c-text-sm c-font-semibold c-mb-0">{t('common.company-name')}</p>
        )}
        {isSmsSentByUser && (
          <p className="c-text-secondary-700 c-text-sm c-font-semibold c-mb-0">
            {t('components.sms.medical-secretariat')}
          </p>
        )}
        <CurecallTooltip
          content={statusLabel.tooltipContent}
          tooltipPosition="right"
          wrapperClassName="c-h-max"
        >
          <CurecallTag status={statusLabel.tagColor} className="tw-text-xs">
            {statusLabel.tagLabel}
          </CurecallTag>
        </CurecallTooltip>
        {isCurrentUserAdmin && linkUrl && (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="c-italic hover:c-underline c-text-primary hover:c-text-primary-500 c-flex c-items-center c-gap-1 c-text-xs"
          >
            <AdminOnlyBadge />
            {t('components.sms.see-config')}
          </a>
        )}
      </div>
      <p className="c-mt-2 c-mb-0 c-text-sm c-rounded-xl c-p-2 c-border c-border-grey-500 c-bg-white c-rounded-tl-none c-whitespace-pre-wrap">
        {smsLog.message}
      </p>
      <p className="c-self-end c-px-2 c-mb-0 c-text-grey-500">
        <TimezoneAwareTime date={smsLog.sentAt} formatter="dateWithHours" />
      </p>
    </div>
  )
}

export { OutboundSmsLogCard }
